import React, { createContext, useContext, useState, useEffect } from "react";
import theme from "../theme";
import darkTheme from "../theme.dark";

import logger from '../utils/logger';

export const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Check localStorage for saved preference, default to light mode
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem("darkMode");
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  // Apply dark mode class to HTML element
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add("dark");
      root.classList.add("prevent-flash");
      root.style.backgroundColor = "#111827"; // Force dark background
    } else {
      root.classList.remove("dark");
      root.classList.remove("prevent-flash");
      root.style.backgroundColor = ""; // Reset to default
    }
  }, [isDarkMode]);

  // Save preference to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem("darkMode", JSON.stringify(isDarkMode));
    } catch (error) {
      logger.error("Failed to save theme preference:", error);
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => !prev);
  };

  const currentTheme = isDarkMode ? darkTheme : theme;

  // Ensure theme has all required properties
  if (!currentTheme.spacing || !currentTheme.colors || !currentTheme.radii) {
    logger.error("Theme is missing required properties:", currentTheme);
  }

  const value = {
    isDarkMode,
    toggleDarkMode,
    theme: currentTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
